import { NgFor, NgIf } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatButton } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatDivider } from "@angular/material/divider";
import { MatTooltip } from "@angular/material/tooltip";
import { finalize, take } from "rxjs/operators";
import { Subscription } from "rxjs";
import { Configuracion } from "src/app/shared/model/emision/configuracion.model";
import { ConfiguracionGeneral } from "src/app/shared/model/emision/configuracionGeneral.model";
import { ConfiguracionService } from "../configuracion.service";
import {
  ConfiguracionNoticeDialogComponent,
  ConfiguracionNoticeDialogData,
} from "../configuracion-notice-dialog/configuracion-notice-dialog.component";
import {
  ConfiguracionFormDialogResult,
  ModalAgregarConfigSecComponent,
} from "../modal-agregar-config-sec/modal-agregar-config-sec.component";

@Component({
  selector: "app-configuraciones",
  standalone: true,
  templateUrl: "./configuraciones.component.html",
  styleUrls: ["./configuraciones.component.css"],
  imports: [MatDivider, NgIf, NgFor, MatTooltip, MatButton],
})
export class ConfiguracionesComponent implements OnInit, OnDestroy {
  selectedHero: unknown;
  selectedConfSec: unknown;
  agregar = false;
  boolSave = false;
  habilitar = false;
  conf: Configuracion;
  listaConfigPrim: Configuracion[] = [];
  confSec = new ConfiguracionGeneral();
  listaConfigSec: ConfiguracionGeneral[] = [];
  nombreConfSec: string = null;
  isDeleting = false;
  isLoadingPrimary = false;
  isLoadingSecondary = false;
  hasLoadedPrimary = false;
  hasLoadedSecondary = false;
  private secondaryRequest?: Subscription;

  constructor(
    private readonly dialog: MatDialog,
    private readonly confService: ConfiguracionService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.boolSave = false;
    this.habilitar = true;
    this.loadPrimaryConfigurations();
  }

  ngOnDestroy(): void {
    this.secondaryRequest?.unsubscribe();
  }

  onSelect(confPrim: string): void {
    if (!confPrim) {
      return;
    }

    this.secondaryRequest?.unsubscribe();
    this.confSec.conf_fol_pr = confPrim;
    this.agregar = false;
    this.hasLoadedSecondary = false;
    this.listaConfigSec = [];
    this.isLoadingSecondary = true;

    this.secondaryRequest = this.confService
      .selectConfiguracionesSec(this.confSec)
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.listaConfigSec = this.obtenerListaRespuesta<ConfiguracionGeneral>(response);
          this.completeSecondaryLoad();
        },
        error: () => {
          this.listaConfigSec = [];
          this.completeSecondaryLoad();
          this.openNotice({
            tone: "error",
            eyebrow: "Consulta no disponible",
            title: "No se cargaron las configuraciones",
            message:
              "No fue posible consultar las configuraciones secundarias. Intente seleccionar la configuración nuevamente.",
            confirmText: "Entendido",
          });
        },
      });
  }

  eliminar(item: ConfiguracionGeneral): void {
    if (!item || this.isDeleting) {
      return;
    }

    this.openNotice({
      tone: "confirmation",
      eyebrow: "Acción irreversible",
      title: "Eliminar configuración",
      message: `Se eliminará la configuración “${
        item.conf_fol_se_dsc || "seleccionada"
      }”. Esta acción no se puede deshacer.`,
      confirmText: "Eliminar",
      cancelText: "Cancelar",
    })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.deleteConfiguration(item);
        }
      });
  }

  openDialogUpdate(item: string | ConfiguracionGeneral): void {
    if (!item) {
      this.openNotice({
        tone: "error",
        eyebrow: "Falta una selección",
        title: "Seleccione una configuración primaria",
        message:
          "Antes de crear una configuración secundaria, seleccione su configuración primaria.",
        confirmText: "Entendido",
      });
      return;
    }

    const dialogRef = this.dialog.open<
      ModalAgregarConfigSecComponent,
      string | ConfiguracionGeneral,
      ConfiguracionFormDialogResult
    >(ModalAgregarConfigSecComponent, {
      autoFocus: "first-tabbable",
      disableClose: true,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "820px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabel: "Configuración secundaria",
      data: item,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.saved) {
        return;
      }

      this.onSelect(result.primaryKey);
      this.openNotice({
        tone: "success",
        eyebrow: "Operación completada",
        title: "Configuración guardada",
        message: "Los datos de la configuración se guardaron correctamente.",
        confirmText: "Aceptar",
      });
    });
  }

  private loadPrimaryConfigurations(): void {
    this.isLoadingPrimary = true;
    this.hasLoadedPrimary = false;
    this.listaConfigPrim = [];

    this.confService
      .selectConfiguraciones(this.conf)
      .pipe(take(1))
      .subscribe({
      next: response => {
        this.listaConfigPrim = this.obtenerListaRespuesta<Configuracion>(response).map(item => ({
          ...item,
          seleccionado: 1,
        }));
        this.completePrimaryLoad();
      },
      error: () => {
        this.listaConfigPrim = [];
        this.completePrimaryLoad();
        this.openNotice({
          tone: "error",
          eyebrow: "Consulta no disponible",
          title: "No se cargaron las configuraciones",
          message:
            "No fue posible consultar las configuraciones primarias. Recargue la página para intentarlo nuevamente.",
          confirmText: "Entendido",
        });
      },
      });
  }

  private obtenerListaRespuesta<T>(response: unknown): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    if (response && typeof response === "object") {
      const list = (response as { list?: unknown }).list;
      return Array.isArray(list) ? (list as T[]) : [];
    }

    return [];
  }

  private completePrimaryLoad(): void {
    this.isLoadingPrimary = false;
    this.hasLoadedPrimary = true;
    this.changeDetectorRef.detectChanges();
  }

  private completeSecondaryLoad(): void {
    this.isLoadingSecondary = false;
    this.hasLoadedSecondary = true;
    this.changeDetectorRef.detectChanges();
  }

  private deleteConfiguration(item: ConfiguracionGeneral): void {
    this.isDeleting = true;

    this.confService
      .deleteConf(item)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.onSelect(this.confSec.conf_fol_pr);
          this.openNotice({
            tone: "success",
            eyebrow: "Operación completada",
            title: "Configuración eliminada",
            message: "La configuración secundaria se eliminó correctamente.",
            confirmText: "Aceptar",
          });
        },
        error: () => {
          this.openNotice({
            tone: "error",
            eyebrow: "No se completó la operación",
            title: "No se pudo eliminar",
            message:
              "La configuración no pudo eliminarse. Verifique su conexión e intente nuevamente.",
            confirmText: "Entendido",
          });
        },
      });
  }

  private openNotice(
    data: ConfiguracionNoticeDialogData
  ): MatDialogRef<ConfiguracionNoticeDialogComponent, boolean> {
    return this.dialog.open(ConfiguracionNoticeDialogComponent, {
      autoFocus: "first-tabbable",
      disableClose: true,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "440px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabel: data.title,
      data,
    });
  }
}
